import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import { I18n } from 'react-redux-i18n';
import isEmpty from 'lodash/isEmpty';
import { formatParam } from 'utils/formatParam';

export type SynthesisQueryOptions = {
  wine?: string;
  brand?: string;
  market?: string;
  region?: string;
};

export type DistributionKpiDpQueryOptions = {
  campaignId: number;
  client_data: number[];
  rivals_data: number[];
  isBranded: boolean;
};

export type DistributionPosOptions = {
  wines?: string[];
  brands?: string[];
  rivals?: string[];
  wine_type?: string;
  competitors?: boolean;
  search?: string;
};

export const useDistributionData = () => {
  const { isLoading, data, refetch } = useTypedQuery('get', '/distribution/');

  return {
    isLoadingDistribution: isLoading,
    distributionData: data || {},
    refetchDistribution: refetch
  };
};

export const useDistributionSynthesisFilters = () => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/synthesis/headers'
  );

  const marketsByZone = data?.markets_by_continent || {};
  return {
    isLoadingFilters: isLoading,
    refetch: refetch,
    filters: {
      clientBrands: data?.client_brands || [],
      clientWines: data?.client_wines || [],
      marketsByZone: marketsByZone,
      zones: Object.keys(marketsByZone)
    }
  };
};

function cleanSynthesisIndicatorData(data: any) {
  if (isEmpty(data)) return {};
  return {
    markets: data.history_markets,
    displayedCurrency: data.displayed_currency,
    avgPrice: data.avg_price,
    dn: data.dn,
    chartData: data.history,
    leaderWine: {
      dn: data.leader_dn,
      name: data.leader_name
    },
    lostStores: data.lost_stores,
    maxRank: data.max_rank,
    newStores: data.new_stores,
    panelEvolution: data.panel_evolution,
    tp: data.penetration_ratio,
    rank: data.rank,
    tableData: data.stats,
    total: data.total
  };
}

export const useDistributionKpiDp = (
  options: DistributionKpiDpQueryOptions,
  enabled: boolean
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/panel',
    {
      params: {
        query: {
          wines: options.isBranded
            ? null
            : formatParam(JSON.stringify(options.client_data)),
          brands: options.isBranded
            ? formatParam(JSON.stringify(options.client_data))
            : null,
          rivals: formatParam(JSON.stringify(options.rivals_data))
        },
        path: {
          campaign_id: options.campaignId
        }
      },
      queryOpts: {
        // Skip if wine or brand not selected
        enabled:
          enabled &&
          Number.isInteger(options.campaignId) &&
          options.campaignId > 0
      }
    }
  );
  return {
    updatingKpiDp: isLoading,
    kpiDp: data?.dp,
    refetch: refetch
  };
};

export const useDistributionSynthesisQuery = (
  options: SynthesisQueryOptions,
  enabled: boolean
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/synthesis',
    {
      params: {
        query: {
          wine: options?.wine,
          brand: options?.brand,
          market: options?.market,
          region: options?.region
        }
      },
      queryOpts: {
        // Skip if wine or brand not selected
        enabled: enabled
      }
    }
  );

  return {
    isLoadingData: isLoading,
    data: {
      restaurants: cleanSynthesisIndicatorData(data?.restaurants),
      caves: cleanSynthesisIndicatorData(data?.caves),
      websites: cleanSynthesisIndicatorData(data?.sites_web)
    },
    refetch: refetch
  };
};

type Option = {
  label: string;
  value: string;
};

export function getMarketsForRegion(
  selectedZone: string,
  marketsOptionsByZone: any
) {
  const allOption: Option = { value: 'all', label: I18n.t('placeholderAll') };

  if (selectedZone === 'world') {
    let marketsOptions: Option[] = [];
    Object.keys(marketsOptionsByZone).forEach((key) => {
      marketsOptions = marketsOptions.concat(marketsOptionsByZone[key]);
    });
    const sortedOptions = marketsOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return [allOption, ...sortedOptions];
  }

  const sortedOptions = marketsOptionsByZone[selectedZone].sort(
    (a: any, b: any) => a.label.localeCompare(b.label)
  );
  return [allOption, ...sortedOptions];
}

export const useDistributionKpi = (
  campaignId: number,
  wineType?: string,
  enabled: boolean = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/kpi',
    {
      params: {
        path: {
          campaign_id: campaignId
        },
        query: {
          wine_type: wineType || undefined
        }
      },
      queryOpts: {
        enabled: enabled && Number.isInteger(campaignId) && campaignId > 0
      }
    }
  );

  return {
    isLoading,
    data,
    refetch
  };
};

export const useDistributionPos = (
  campaignId: number,
  options: DistributionPosOptions = {},
  enabled: boolean = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/pos',
    {
      params: {
        path: {
          campaign_id: campaignId
        },
        query: {
          wines: options.wines?.length
            ? formatParam(JSON.stringify(options.wines))
            : undefined,
          brands: options.brands?.length
            ? formatParam(JSON.stringify(options.brands))
            : undefined,
          wine_type: options.wine_type || undefined
        }
      },
      queryOpts: {
        enabled: enabled && Number.isInteger(campaignId) && campaignId > 0
      }
    }
  );

  return {
    isLoading,
    data,
    refetch
  };
};

export type DistributionPriceOptions = {
  wines?: string[];
  rivals?: string[];
  wine_type?: string;
};

export const useDistributionPrice = (
  campaignId: number,
  options: DistributionPriceOptions = {},
  enabled: boolean = true
) => {
  const { isLoading, isFetching, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/price',
    {
      params: {
        path: {
          campaign_id: campaignId
        },
        query: {
          wines: options.wines?.length
            ? formatParam(JSON.stringify(options.wines))
            : undefined,
          rivals: options.rivals?.length
            ? formatParam(JSON.stringify(options.rivals))
            : undefined,
          wine_type: options.wine_type || undefined
        }
      },
      queryOpts: {
        enabled: enabled && Number.isInteger(campaignId) && campaignId > 0
      }
    }
  );

  return {
    isLoading,
    isFetching,
    data,
    refetch
  };
};

export type DistributionVintageOptions = {
  wines?: string[];
  brands?: string[];
};

export const useDistributionVintage = (
  campaignId: number,
  options: DistributionVintageOptions = {},
  enabled: boolean = true
) => {
  const { isLoading, isFetching, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/vintage',
    {
      params: {
        path: {
          campaign_id: campaignId
        },
        query: {
          wines: options.wines?.length
            ? formatParam(JSON.stringify(options.wines))
            : undefined,
          brands: options.brands?.length
            ? formatParam(JSON.stringify(options.brands))
            : undefined
        }
      },
      queryOpts: {
        enabled: enabled && Number.isInteger(campaignId) && campaignId > 0
      }
    }
  );

  return {
    isLoading,
    isFetching,
    data,
    refetch
  };
};

export const useDistributionRaw = (
  campaignId: number,
  options: DistributionPosOptions = {},
  enabled: boolean = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/{campaign_id}/raw',
    {
      params: {
        path: {
          campaign_id: campaignId
        },
        query: {
          wines: options.wines?.length
            ? formatParam(JSON.stringify(options.wines))
            : undefined,
          brands: options.brands?.length
            ? formatParam(JSON.stringify(options.brands))
            : undefined,
          rivals: options.rivals?.length
            ? formatParam(JSON.stringify(options.rivals))
            : undefined,
          wine_type: options.wine_type || undefined,
          competitors: options.competitors || undefined,
          search: options.search || undefined
        }
      },
      queryOpts: {
        enabled: enabled && Number.isInteger(campaignId) && campaignId > 0
      }
    }
  );

  return {
    isLoading,
    data,
    refetch
  };
};

export const useDistributionStore = (
  storeId: string,
  enabled: boolean = true
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/distribution/store/{store_id}',
    {
      params: {
        path: {
          store_id: parseInt(storeId, 10)
        }
      },
      queryOpts: {
        enabled: enabled && !!storeId && !isNaN(parseInt(storeId, 10))
      }
    }
  );

  return {
    isLoading,
    data,
    refetch
  };
};
